import React, { ReactNode } from "react";
import { useTheme } from "styled-components";
import { TradeData } from "@shared/types";
import useMeasure from "utils/hooks/useMeasure";
import useGridProgressGraph, {
  DEFAULT_CHART_HEIGHT,
  LINE_START_Y,
  LINE_X,
} from "features/trades/common/useGridProgressGraph.tsx";

interface GridProgressGraphProps {
  tradeData: TradeData;
}

const GridProgressGraph = ({ tradeData }: GridProgressGraphProps) => {
  const theme = useTheme();
  const [measureCurrentPriceRef, currentPriceSize] = useMeasure<any>();
  const {
    linesData,
    getCurrentPriceData,
    minPrice,
    maxPrice,
    currentPrice,
    baseTextProps,
    lineHeight,
  } = useGridProgressGraph(tradeData, currentPriceSize);

  const getLine = (color: string, startY: number, height: number) => {
    return (
      <line
        x1={LINE_X}
        y1={startY}
        x2={LINE_X}
        y2={startY + height}
        stroke={color}
        strokeLinecap="round"
        strokeWidth={3}
      />
    );
  };

  const getOutsideRangeLine = () => {
    return getLine("#D9DDE2", LINE_START_Y, lineHeight);
  };

  const { greenPixelData, redPixelData, minPriceY, maxPriceY, currentPriceY } =
    linesData;

  const getColoredLines = () => {
    let greenLine: ReactNode = undefined;
    let redLine: ReactNode = undefined;
    if (redPixelData) {
      redLine = getLine(
        theme.colors.red,
        redPixelData.yStart,
        redPixelData.height
      );
    }
    if (greenPixelData) {
      greenLine = getLine(
        theme.colors.green,
        greenPixelData.yStart,
        greenPixelData.height
      );
    }
    return (
      <>
        {redLine}
        {greenLine}
      </>
    );
  };

  const getRangePrices = () => {
    return (
      <>
        <text
          {...baseTextProps}
          y={minPriceY}
          fill={theme.colors.secondaryGrey}
        >
          {minPrice}
        </text>
        <text
          {...baseTextProps}
          y={maxPriceY}
          fill={theme.colors.secondaryGrey}
        >
          {maxPrice}
        </text>
      </>
    );
  };

  const getCurrentPrice = () => {
    const currentPriceData = getCurrentPriceData();
    return (
      <>
        <path d={currentPriceData.path} fill={currentPriceData.color} />
        <text
          {...baseTextProps}
          y={currentPriceY}
          fill={theme.colors.primaryBlue}
          ref={measureCurrentPriceRef}
        >
          {currentPrice}
        </text>
      </>
    );
  };

  return (
    <div style={{ height: DEFAULT_CHART_HEIGHT }}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 100 ${DEFAULT_CHART_HEIGHT}`}
        width="100"
        height={DEFAULT_CHART_HEIGHT}
      >
        {getOutsideRangeLine()}
        {getColoredLines()}
        {getRangePrices()}
        {getCurrentPrice()}
      </svg>
    </div>
  );
};

export default React.memo(GridProgressGraph);
